/**
 * Subscription success page - shown after successful Stripe Checkout
 * Handles post-purchase flow and subscription status updates
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../lib/firebase';
import {
  useSubscription,
  usePurchase,
  useBillingStatus,
} from '../services/subscription';
import { LoadingSpinner } from '../components/ui';

import { subscriptionStatusLabel } from '../services/subscription/statusLabel';

export function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCheckingBilling, setIsCheckingBilling] = useState(true);
  const billing = useBillingStatus();
  const { refetch: refetchBilling } = billing;
  const billingStatus =
    isCheckingBilling || billing.isLoading || billing.isError
      ? undefined
      : billing.data;
  const statusLabel = subscriptionStatusLabel(billingStatus);
  const { subscription, refetch, error: subscriptionError } = useSubscription();
  const { restorePurchases } = usePurchase();
  const [isRefetching, setIsRefetching] = useState(true);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const confirmed =
    !refreshFailed &&
    !subscriptionError &&
    billingStatus?.has_subscription &&
    (billingStatus.subscription_status === 'trialing' ||
      billingStatus.subscription_status === 'active');
  const checkingBilling = isCheckingBilling || billing.isLoading;

  // Note: Using 'sid' instead of 'session_id' to avoid URL truncation issue
  const sessionId = searchParams.get('sid');

  useEffect(() => {
    // Refresh subscription data to get latest status
    const refreshSubscription = async () => {
      setIsRefetching(true);
      setIsCheckingBilling(true);

      // Force token refresh to pick up updated custom claims (including new entitlements)
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (error) {
          console.error('Token refresh failed (non-critical):', error);
        }
      }

      try {
        // First sync with Stripe to get latest subscription status
        // This is a best-effort recovery mechanism - webhooks are the primary sync path
        await restorePurchases();
      } catch (error) {
        console.error(
          'Stripe sync failed (non-critical - webhooks handle primary sync):',
          error
        );
        // Don't throw - allow page to render even if sync fails
        // Webhooks have already processed the subscription
      }

      try {
        // Refetch subscription data to update the UI
        await refetch();
        // Billing confirmation must not hold the account actions behind the
        // loading screen. Until this fresh read settles, show a neutral status.
        void refetchBilling()
          .then(result => setRefreshFailed(!!result?.isError))
          .catch(error => {
            setRefreshFailed(true);
            console.error('Failed to refetch billing status:', error);
          })
          .finally(() => setIsCheckingBilling(false));
      } catch (error) {
        setIsCheckingBilling(false);
        setRefreshFailed(true);
        console.error('Failed to refetch subscription:', error);
        // Page will render without subscription details if refetch fails
      } finally {
        // Always clear loading state to prevent infinite loading
        setIsRefetching(false);
      }
    };

    // Add a small delay to allow webhook processing
    const timer = setTimeout(refreshSubscription, 2000);
    return () => clearTimeout(timer);
  }, [refetch, refetchBilling, restorePurchases]);

  // The Stripe-billed entitlement, selected as the dashboard selects it. The
  // details card is about a Stripe subscription, so it is not shown for
  // access held any other way.
  const stripeEntitlement = subscription?.entitlements
    ? Object.values(subscription.entitlements).find(
        e => e.isActive && e.hasStripeSubscription
      )
    : undefined;
  // Stripe's own period end, and only while the subscription renews. The
  // entitlement's `periodEndDate` is merged across every source of access
  // and is not a billing date (#2209).
  const nextBillingDate =
    billingStatus?.has_subscription &&
    billingStatus.subscription_status &&
    billingStatus.subscription_status !== 'trialing' &&
    stripeEntitlement?.willRenew
      ? stripeEntitlement.stripePeriodEndDate
      : undefined;
  // The plan and its price as this user's own subscription has them
  // (`GET /v1/billing/status`), or nothing: no catalogue name and no
  // stand-in (#2463).
  const planName = billingStatus?.has_subscription
    ? billingStatus.plan_name
    : null;
  const planPrice = billingStatus?.has_subscription
    ? billingStatus.amount
    : null;

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleManageSubscription = () => {
    navigate('/subscriptions');
  };

  if (isRefetching) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Finalizing your subscription...
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            This will just take a moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          {/* Success Icon */}
          {confirmed && (
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20">
                <svg
                  className="h-8 w-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          )}

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {confirmed
              ? 'Subscription started'
              : checkingBilling
                ? 'Checking your subscription'
                : 'Subscription'}
          </h1>
          {(subscriptionError ||
            billing.isError ||
            refreshFailed ||
            (!checkingBilling && !confirmed)) && (
            <p role="alert">
              Unable to confirm your subscription. Please try again or contact
              support.
            </p>
          )}
          {statusLabel && <p className="mb-8">{statusLabel}</p>}

          {/* Subscription Details: shown whenever there is a row to show.
              The plan and price come from the billing status, so they need
              no Stripe entitlement, as for a past_due subscription or one
              whose entitlement has not arrived yet (#2463). */}
          {(planName || planPrice || nextBillingDate) && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Subscription Details
              </h2>
              <div className="space-y-2 text-sm">
                {planName && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Plan:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {planName}
                    </span>
                  </div>
                )}
                {planPrice && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Plan price:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {planPrice}
                    </span>
                  </div>
                )}
                {nextBillingDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Next billing:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {nextBillingDate.toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={handleContinue} className="btn-primary">
              Continue to Dashboard
            </button>
            <button
              onClick={handleManageSubscription}
              className="btn-secondary"
            >
              Manage Subscription
            </button>
          </div>

          {/* Additional Information */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Questions about your subscription?
              <button
                onClick={() => navigate('/contact')}
                className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
              >
                Contact Support
              </button>
            </p>
            {sessionId && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Session ID: {sessionId}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
